import React, { useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as YouTubeIcon } from '../../assets/icons/youtube-icon.svg';
import { ReactComponent as YouTubeText } from '../../assets/icons/youtube-text.svg';
import { ReactComponent as TwitchIcon } from '../../assets/icons/twitch-icon.svg';
import { ReactComponent as TwitchText } from '../../assets/icons/twitch-text.svg';
import { ReactComponent as InstagramIcon } from '../../assets/icons/instagram-icon.svg';
import { ReactComponent as InstagramText } from '../../assets/icons/instagram-text.svg';
import { PLATFORMS, getPlatformUrl } from '../../constants';
import { authApi } from '../../services/authApi';
import './ConnectedPlatforms.css';

const PLATFORM_SVG_MAP = {
  youtube:   { Icon: YouTubeIcon,   Text: YouTubeText,   textW: 88 },
  twitch:    { Icon: TwitchIcon,    Text: TwitchText,    textW: 89,  iconViewBox: '0 -1.5 18 19' },
  instagram: { Icon: InstagramIcon, Text: InstagramText, textW: 101 },
};

const VERIFIED_PLATFORM_LINKING_ENABLED =
  String(process.env.REACT_APP_ENABLE_VERIFIED_PLATFORM_LINKING).toLowerCase() === 'true';
const VERIFIED_PLATFORMS = ['twitch', 'youtube'];
const EDIT_PLATFORMS = ['instagram', 'twitch', 'youtube'];

const ConnectedPlatforms = React.forwardRef(function ConnectedPlatforms(
  { accounts, onAddAccount, onRemoveAccount, onAccountsUpdate, editMode = false },
  ref,
) {
  const [connecting, setConnecting] = useState(null);
  const [toast, setToast] = useState(null);

  const [inputs, setInputs] = useState(() => {
    const init = {};
    EDIT_PLATFORMS.forEach((pid) => {
      const acc = accounts.find((a) => a.platformId === pid);
      init[pid] = acc?.username || '';
    });
    return init;
  });

  const editablePlatformIds = VERIFIED_PLATFORM_LINKING_ENABLED
    ? EDIT_PLATFORMS.filter((platformId) => !VERIFIED_PLATFORMS.includes(platformId))
    : EDIT_PLATFORMS;

  useEffect(() => {
    setInputs(() => {
      const next = {};
      editablePlatformIds.forEach((pid) => {
        const acc = accounts.find((a) => a.platformId === pid);
        next[pid] = acc?.username || '';
      });
      return next;
    });
  }, [accounts, editablePlatformIds]);

  useImperativeHandle(ref, () => ({
    commitChanges: async () => {
      const ops = editablePlatformIds.map(async (pid) => {
        const current = accounts.find((a) => a.platformId === pid);
        const next = inputs[pid].trim();
        const prev = current?.username || '';
        if (next === prev) return;
        if (!next && current) {
          await onRemoveAccount(pid);
        } else if (next) {
          await onAddAccount({ platformId: pid, username: next });
        }
      });
      await Promise.all(ops);
    },
  }), [accounts, editablePlatformIds, inputs, onAddAccount, onRemoveAccount]);

  useEffect(() => {
    if (!VERIFIED_PLATFORM_LINKING_ENABLED || typeof window === 'undefined') return undefined;

    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const error = params.get('error');
    const warning = params.get('warning');
    if (!connected && !error && !warning) return undefined;

    const showToast = (msg, type = 'success') => {
      setToast({ message: msg, type });
      window.setTimeout(() => setToast(null), 3500);
    };

    if (connected === 'twitch') {
      if (warning === 'twitch_live_sync_failed') {
        showToast('Twitch account connected, but automatic live sync setup needs attention. You can still use PolarAce Go Live for now.', 'error');
      } else {
        showToast('Twitch account connected');
      }
      onAccountsUpdate?.();
    }
    else if (connected === 'youtube') { showToast('YouTube channel connected'); onAccountsUpdate?.(); }
    else if (error === 'twitch_already_linked') showToast('That Twitch account is already linked to another PolarAce user.', 'error');
    else if (error === 'twitch_user_not_found') showToast('Your PolarAce user record could not be found during Twitch connect. Please sign in again.', 'error');
    else if (error === 'twitch_connect_unavailable') showToast('Twitch connect is temporarily unavailable. Please try again in a moment.', 'error');
    else if (error === 'twitch_connect_failed') showToast('Twitch connection failed. Please try again.', 'error');
    else if (error === 'twitch_token_failed') showToast('Twitch token error. Please try again.', 'error');
    else if (error === 'youtube_connect_failed') showToast('YouTube connection failed. Please try again.', 'error');
    else if (error === 'youtube_token_failed') showToast('YouTube token error. Please try again.', 'error');
    else if (error === 'no_youtube_channel') showToast('No YouTube channel was found on that Google account.', 'error');

    window.history.replaceState(window.history.state, '', window.location.pathname);
    return undefined;
  }, [onAccountsUpdate]);

  const getPlatformInfo = (pid) => PLATFORMS.find((p) => p.id === pid);
  const formatPlatformUsername = (account) => {
    if (!account?.username) return '';
    if (account.platformId === 'youtube' && !String(account.username).startsWith('channel/')) {
      return `@${account.username}`;
    }
    if (account.platformId === 'tiktok' && !String(account.username).startsWith('@')) {
      return `@${account.username}`;
    }
    if (account.platformId === 'twitch' && !String(account.username).startsWith('@')) {
      return `@${account.username}`;
    }
    return account.username;
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  };

  const handleVerifiedConnect = async (platformId) => {
    try {
      setConnecting(platformId);
      const data = await authApi.startPlatformConnect(platformId);
      const redirectUrl = typeof data?.redirectUrl === 'string' ? data.redirectUrl.trim() : '';
      if (!redirectUrl) {
        throw new Error('Missing redirect URL');
      }
      window.location.href = redirectUrl;
    } catch (error) {
      setConnecting(null);
      showToast(error.message || `Failed to connect ${platformId}`, 'error');
    }
  };

  const toastEl = toast && (
    <div className={`connected-platforms__toast connected-platforms__toast--${toast.type}`}>
      {toast.message}
    </div>
  );

  /* ── Edit mode ───────────────────────────────────────────────────────── */
  if (editMode) {
    if (VERIFIED_PLATFORM_LINKING_ENABLED) {
      const verifiedPlatforms = VERIFIED_PLATFORMS
        .map((platformId) => getPlatformInfo(platformId))
        .filter(Boolean);

      return (
        <div className="connected-platforms connected-platforms--verified-mode">
          {toastEl}
          <div className="connected-platforms__verified-grid">
            {verifiedPlatforms.map((platform) => {
              const account = accounts.find((item) => item.platformId === platform.id);
              const profileUrl = account?.profileUrl || getPlatformUrl(platform.id, account?.username);
              const svgs = PLATFORM_SVG_MAP[platform.id];

              return (
                <div
                  key={platform.id}
                  className={`connected-platforms__verified-card${
                    account ? ' connected-platforms__verified-card--connected' : ''
                  }`}
                >
                  <div className="connected-platforms__verified-main">
                    <div className="connected-platforms__verified-label">
                      {svgs ? (
                        <>
                          <svgs.Icon
                            className="connected-platforms__badge-icon connected-platforms__verified-badge-icon"
                            {...(svgs.iconViewBox ? { viewBox: svgs.iconViewBox } : {})}
                          />
                          <svgs.Text
                            viewBox={`0 0.5 ${svgs.textW} 10.5`}
                            width={svgs.textW}
                            height="10.5"
                            className="connected-platforms__badge-text connected-platforms__verified-badge-text"
                          />
                        </>
                      ) : (
                        <span
                          className="connected-platforms__verified-icon"
                          style={{ color: platform.color }}
                        >
                          {platform.icon}
                        </span>
                      )}
                    </div>
                    <div className="connected-platforms__verified-copy">
                      {account ? (
                        <a
                          className="connected-platforms__verified-username"
                          href={profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {formatPlatformUsername(account)}
                        </a>
                      ) : (
                        <span className="connected-platforms__verified-hint">Not connected</span>
                      )}
                    </div>
                  </div>

                  <div className="connected-platforms__verified-actions">
                    {account ? (
                      <>
                        <span className="connected-platforms__verified-badge">Verified</span>
                        <button
                          type="button"
                          className="connected-platforms__verified-btn connected-platforms__verified-btn--disconnect"
                          onClick={() => onRemoveAccount(platform.id)}
                        >
                          Disconnect
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="connected-platforms__verified-btn connected-platforms__verified-btn--connect"
                        onClick={() => handleVerifiedConnect(platform.id)}
                        disabled={connecting === platform.id}
                      >
                        {connecting === platform.id ? 'Connecting…' : `Connect ${platform.name}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {editablePlatformIds.length > 0 && (
            <div className="connected-platforms__edit-rows">
              {editablePlatformIds.map((pid) => {
                const svgs = PLATFORM_SVG_MAP[pid];
                const platform = getPlatformInfo(pid);
                return (
                  <div key={pid} className="connected-platforms__edit-row">
                    <div className="connected-platforms__edit-label">
                      {svgs && (
                        <svgs.Icon
                          className="connected-platforms__badge-icon"
                          {...(svgs.iconViewBox ? { viewBox: svgs.iconViewBox } : {})}
                        />
                      )}
                      {svgs && (
                        <svgs.Text
                          viewBox={`0 0.5 ${svgs.textW} 10.5`}
                          width={svgs.textW}
                          height="10.5"
                          className="connected-platforms__badge-text"
                        />
                      )}
                    </div>
                    <input
                      type="text"
                      className="connected-platforms__username-input"
                      placeholder={platform?.name || pid}
                      value={inputs[pid] || ''}
                      onChange={(e) => setInputs((prev) => ({ ...prev, [pid]: e.target.value }))}
                      aria-label={`${platform?.name || pid} username`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="connected-platforms">
        {toastEl}
        <div className="connected-platforms__edit-rows">
          {editablePlatformIds.map((pid) => {
            const svgs = PLATFORM_SVG_MAP[pid];
            const platform = getPlatformInfo(pid);
            return (
              <div key={pid} className="connected-platforms__edit-row">
                <div className="connected-platforms__edit-label">
                  {svgs && (
                    <svgs.Icon
                      className="connected-platforms__badge-icon"
                      {...(svgs.iconViewBox ? { viewBox: svgs.iconViewBox } : {})}
                    />
                  )}
                  {svgs && (
                    <svgs.Text
                      viewBox={`0 0.5 ${svgs.textW} 10.5`}
                      width={svgs.textW}
                      height="10.5"
                      className="connected-platforms__badge-text"
                    />
                  )}
                </div>
                <input
                  type="text"
                  className="connected-platforms__username-input"
                  placeholder={platform?.name || pid}
                  value={inputs[pid]}
                  onChange={(e) => setInputs((prev) => ({ ...prev, [pid]: e.target.value }))}
                  aria-label={`${platform?.name || pid} username`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── View mode: SVG badge row ────────────────────────────────────────── */
  if (accounts.length === 0) return null;

  return (
    <div className="connected-platforms">
      {toastEl}
      <div className="connected-platforms__badge-row">
        {accounts.map((account) => {
          const platform = getPlatformInfo(account.platformId);
          if (!platform) return null;
          const profileUrl = account.profileUrl || getPlatformUrl(account.platformId, account.username);
          const svgs = PLATFORM_SVG_MAP[account.platformId];

          return (
            <a
              key={account.platformId}
              className="connected-platforms__badge"
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={platform.name}
            >
              {svgs ? (
                <>
                  <svgs.Icon
                    className="connected-platforms__badge-icon"
                    {...(svgs.iconViewBox ? { viewBox: svgs.iconViewBox } : {})}
                  />
                  <svgs.Text
                    viewBox={`0 0.5 ${svgs.textW} 10.5`}
                    width={svgs.textW}
                    height="10.5"
                    className="connected-platforms__badge-text"
                  />
                </>
              ) : (
                <>
                  <span className="connected-platforms__badge-fallback-icon" style={{ color: platform.color }}>
                    {platform.icon}
                  </span>
                  <span className="connected-platforms__badge-fallback-name">
                    {platform.name.toUpperCase()}
                  </span>
                </>
              )}
            </a>
          );
        })}
      </div>
    </div>
  );
});

ConnectedPlatforms.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      platformId: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onAddAccount: PropTypes.func.isRequired,
  onRemoveAccount: PropTypes.func.isRequired,
  onAccountsUpdate: PropTypes.func,
  editMode: PropTypes.bool,
};

ConnectedPlatforms.defaultProps = {
  onAccountsUpdate: undefined,
  editMode: false,
};

export default ConnectedPlatforms;
